/**
 * Behavioral Deployment Command Layer
 * ----------------------------------------------------------------
 * Operational lifecycle engine. Tracks deployment state for
 * campaigns and assets, detects fatigue/saturation/decay, and
 * feeds reality back into:
 *   - generation_biases
 *   - adaptive_learning_profiles
 *   - viral_prediction_models  (read)
 *   - generation_strategy_cycles (read)
 *
 * No UI lives here.
 */
import { supabase } from "@/integrations/supabase/client";

export const DEPLOYMENT_EVENT = "behavioral-deployment:changed";
export const DEPLOYMENT_DECAY_EVENT = "behavioral-deployment:decay";

export type DeploymentStatus =
  | "staged"
  | "queued"
  | "rendering"
  | "rendered"
  | "scheduled"
  | "deployed"
  | "live"
  | "saturating"
  | "declining"
  | "retired";

export const DEPLOYMENT_STATUSES: DeploymentStatus[] = [
  "staged", "queued", "rendering", "rendered", "scheduled",
  "deployed", "live", "saturating", "declining", "retired",
];

export const STATUS_TONE: Record<DeploymentStatus, string> = {
  staged:     "border-muted-foreground/40 text-muted-foreground bg-muted-foreground/10",
  queued:     "border-cyan/30 text-cyan/80 bg-cyan/5",
  rendering:  "border-cyan/50 text-cyan bg-cyan/10 animate-pulse",
  rendered:   "border-emerald-400/50 text-emerald-300 bg-emerald-400/10",
  scheduled:  "border-violet-400/50 text-violet-300 bg-violet-400/10",
  deployed:   "border-cyan/60 text-cyan bg-cyan/15",
  live:       "border-emerald-500/60 text-emerald-300 bg-emerald-500/15 animate-pulse",
  saturating: "border-amber-400/60 text-amber-300 bg-amber-400/15",
  declining:  "border-orange-500/60 text-orange-300 bg-orange-500/15",
  retired:    "border-red-500/50 text-red-400 bg-red-500/10",
};

export interface DeploymentCampaignRow {
  id: string;
  campaign_id: string | null;
  deployment_status: DeploymentStatus;
  dominant_platform: string | null;
  projected_reach: number;
  projected_retention: number;
  projected_replay_velocity: number;
  deployment_confidence: number;
  behavioral_saturation: number;
  fatigue_risk: number;
  audience_conditioning_score: number;
  decay_score: number;
  rationale: string | null;
  launched_at: string | null;
  retired_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface DeploymentAssetRow {
  id: string;
  asset_id: string | null;
  deployment_campaign_id: string | null;
  deployment_status: DeploymentStatus;
  platform: string | null;
  deployment_priority: number;
  projected_performance: number;
  actual_performance: number;
  saturation_state: "fresh" | "rising" | "peaking" | "saturating" | "burnt";
  fatigue_state: "low" | "moderate" | "elevated" | "critical";
  decay_score: number;
  launched_at: string | null;
  retired_at: string | null;
  created_at: string;
  updated_at: string;
}

const emit = (name: string, detail?: any) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(name, { detail }));
};

// ── Fetchers ──────────────────────────────────────────────
export async function fetchDeploymentCampaigns(): Promise<DeploymentCampaignRow[]> {
  const { data } = await (supabase as any)
    .from("deployment_campaigns")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(40);
  return (data ?? []) as DeploymentCampaignRow[];
}

export async function fetchDeploymentAssets(): Promise<DeploymentAssetRow[]> {
  const { data } = await (supabase as any)
    .from("deployment_assets")
    .select("*")
    .order("deployment_priority", { ascending: false })
    .order("created_at", { ascending: false })
    .limit(80);
  return (data ?? []) as DeploymentAssetRow[];
}

// ── Stage from existing campaigns ─────────────────────────
export async function stageDeploymentsFromCampaigns(): Promise<number> {
  const { data: camps } = await (supabase as any)
    .from("behavioral_campaigns")
    .select("id, dominant_platform, projected_virality, projected_retention, audience_conditioning_score, fatigue_risk, rationale")
    .eq("status", "active")
    .order("created_at", { ascending: false })
    .limit(8);

  const list = (camps ?? []) as any[];
  if (!list.length) return 0;

  const { data: existing } = await (supabase as any)
    .from("deployment_campaigns")
    .select("campaign_id")
    .in("campaign_id", list.map((c) => c.id));
  const seen = new Set((existing ?? []).map((r: any) => r.campaign_id));

  const rows = list
    .filter((c) => !seen.has(c.id))
    .map((c) => ({
      campaign_id: c.id,
      deployment_status: "staged",
      dominant_platform: c.dominant_platform,
      projected_reach: clamp(Number(c.projected_virality || 0) * 1.2),
      projected_retention: Number(c.projected_retention || 0),
      projected_replay_velocity: clamp(Number(c.projected_virality || 0) * 0.85 + 8),
      deployment_confidence: clamp(60 + Math.random() * 25),
      behavioral_saturation: 0,
      fatigue_risk: Number(c.fatigue_risk || 0),
      audience_conditioning_score: Number(c.audience_conditioning_score || 0),
      decay_score: 0,
      rationale: c.rationale ?? null,
    }));

  if (!rows.length) return 0;

  const { data: inserted } = await (supabase as any)
    .from("deployment_campaigns")
    .insert(rows)
    .select("id, campaign_id, dominant_platform");

  // Stage child assets from campaign_asset_sequences
  for (const dc of inserted ?? []) {
    const { data: seq } = await (supabase as any)
      .from("campaign_asset_sequences")
      .select("asset_id, sequence_order, projected_replay_impact, projected_retention_impact")
      .eq("campaign_id", dc.campaign_id)
      .order("sequence_order", { ascending: true });
    const assetRows = (seq ?? []).map((s: any, idx: number) => ({
      asset_id: s.asset_id,
      deployment_campaign_id: dc.id,
      deployment_status: "staged",
      platform: dc.dominant_platform,
      deployment_priority: 10 - Math.min(idx, 9),
      projected_performance: clamp(
        (Number(s.projected_replay_impact || 0) + Number(s.projected_retention_impact || 0)) / 2,
      ),
      actual_performance: 0,
      saturation_state: "fresh",
      fatigue_state: "low",
      decay_score: 0,
    }));
    if (assetRows.length) {
      await (supabase as any).from("deployment_assets").insert(assetRows);
    }
  }

  emit(DEPLOYMENT_EVENT, { staged: rows.length });
  return rows.length;
}

// ── Mutations ─────────────────────────────────────────────
export async function setCampaignStatus(id: string, status: DeploymentStatus) {
  const patch: any = { deployment_status: status };
  if (status === "live" || status === "deployed") patch.launched_at = new Date().toISOString();
  if (status === "retired") patch.retired_at = new Date().toISOString();
  await (supabase as any).from("deployment_campaigns").update(patch).eq("id", id);
  // cascade child assets if retiring or going live
  if (status === "retired" || status === "live") {
    await (supabase as any)
      .from("deployment_assets")
      .update(patch)
      .eq("deployment_campaign_id", id);
  }
  emit(DEPLOYMENT_EVENT, { id, status });
}

export async function setAssetStatus(id: string, status: DeploymentStatus) {
  const patch: any = { deployment_status: status };
  if (status === "live" || status === "deployed") patch.launched_at = new Date().toISOString();
  if (status === "retired") patch.retired_at = new Date().toISOString();
  await (supabase as any).from("deployment_assets").update(patch).eq("id", id);
  emit(DEPLOYMENT_EVENT, { id, status });
}

export async function adjustAssetPriority(id: string, delta: number) {
  const { data } = await (supabase as any)
    .from("deployment_assets")
    .select("deployment_priority")
    .eq("id", id)
    .single();
  const next = Math.max(1, Math.min(10, Number(data?.deployment_priority ?? 5) + delta));
  await (supabase as any).from("deployment_assets").update({ deployment_priority: next }).eq("id", id);
  emit(DEPLOYMENT_EVENT, { id, priority: next });
}

// ── Decay scan ────────────────────────────────────────────
/**
 * Scan live deployments and detect fatigue / saturation /
 * decline. Pulls recent telemetry from platform_signal_ingestion
 * to attribute reality. Updates deployment rows + writes a
 * negative-weight bias for any structure entering decay so the
 * generation engine pivots away.
 */
export interface DecaySignal {
  scope: "campaign" | "asset" | "structure";
  label: string;
  trend: "saturating" | "declining" | "fatigue" | "burnout";
  platform: string | null;
  intensity: number; // 0-100
  note: string;
}

export async function runDecayScan(): Promise<DecaySignal[]> {
  const signals: DecaySignal[] = [];

  // 1. Telemetry-driven structural decay (last 200 rows)
  const { data: tel } = await (supabase as any)
    .from("platform_signal_ingestion")
    .select("platform, archetype, format, escalation_path, replay_rate, completion_rate, virality_score, predicted_virality, collected_at")
    .order("collected_at", { ascending: false })
    .limit(200);

  const groups = new Map<string, any[]>();
  for (const r of (tel ?? []) as any[]) {
    const key = `${r.platform}::${r.archetype ?? "—"}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(r);
  }

  for (const [key, rows] of groups) {
    if (rows.length < 4) continue;
    const half = Math.floor(rows.length / 2);
    const recent = rows.slice(0, half);
    const prior = rows.slice(half);
    const avgRecent = avg(recent.map((r) => Number(r.replay_rate || 0)));
    const avgPrior = avg(prior.map((r) => Number(r.replay_rate || 0)));
    const delta = avgPrior - avgRecent;
    if (delta > 6) {
      const [platform, archetype] = key.split("::");
      const intensity = Math.min(100, Math.round(delta * 6));
      signals.push({
        scope: "structure",
        label: archetype,
        trend: delta > 14 ? "burnout" : "declining",
        platform,
        intensity,
        note: `${archetype} replay decay accelerating on ${platform}`,
      });
      // Push negative bias to steer generation away
      await (supabase as any).from("generation_biases").insert({
        dimension: "archetype",
        key: archetype,
        bias_direction: "decrease",
        weight: Math.min(1.4, 0.4 + intensity / 100),
        confidence: Math.min(0.9, 0.4 + intensity / 200),
        evidence_score: intensity,
        active: true,
        rationale: `Auto-decay: ${archetype} declining on ${platform} (Δreplay −${delta.toFixed(1)}%)`,
      });
    }
  }

  // 2. Campaign-level fatigue evaluation
  const camps = await fetchDeploymentCampaigns();
  for (const c of camps) {
    if (c.deployment_status === "retired") continue;
    const ageHrs = (Date.now() - new Date(c.launched_at ?? c.created_at).getTime()) / 36e5;
    const expectedSat = Math.min(95, Math.round(ageHrs * 1.8 + (c.fatigue_risk * 0.4)));
    const decay = Math.max(0, expectedSat - 35);
    let nextStatus: DeploymentStatus = c.deployment_status;
    if (expectedSat > 80) nextStatus = "declining";
    else if (expectedSat > 55) nextStatus = "saturating";
    if (nextStatus !== c.deployment_status && c.deployment_status === "live") {
      await (supabase as any)
        .from("deployment_campaigns")
        .update({ deployment_status: nextStatus, behavioral_saturation: expectedSat, decay_score: decay })
        .eq("id", c.id);
      signals.push({
        scope: "campaign",
        label: c.dominant_platform ?? "campaign",
        trend: nextStatus === "declining" ? "declining" : "saturating",
        platform: c.dominant_platform,
        intensity: expectedSat,
        note: `${c.dominant_platform ?? "Campaign"} entering ${nextStatus} (${expectedSat}% saturation)`,
      });
    } else if (expectedSat !== c.behavioral_saturation) {
      await (supabase as any)
        .from("deployment_campaigns")
        .update({ behavioral_saturation: expectedSat, decay_score: decay })
        .eq("id", c.id);
    }
  }

  // 3. Asset-level fatigue
  const assets = await fetchDeploymentAssets();
  for (const a of assets) {
    if (a.deployment_status === "retired") continue;
    const ageHrs = (Date.now() - new Date(a.launched_at ?? a.created_at).getTime()) / 36e5;
    const sat = Math.min(100, Math.round(ageHrs * 2.4));
    const fatigue =
      sat > 80 ? "critical" : sat > 60 ? "elevated" : sat > 35 ? "moderate" : "low";
    const satState =
      sat > 90 ? "burnt" : sat > 70 ? "saturating" : sat > 45 ? "peaking" : sat > 20 ? "rising" : "fresh";
    if (fatigue !== a.fatigue_state || satState !== a.saturation_state) {
      await (supabase as any)
        .from("deployment_assets")
        .update({ fatigue_state: fatigue, saturation_state: satState, decay_score: sat })
        .eq("id", a.id);
    }
    if (sat > 92) {
      await setAssetStatus(a.id, "retired");
      signals.push({
        scope: "asset",
        label: a.platform ?? "asset",
        trend: "burnout",
        platform: a.platform,
        intensity: sat,
        note: `Asset ${a.id.slice(0, 6)} retired — fatigue critical`,
      });
    }
  }

  emit(DEPLOYMENT_DECAY_EVENT, { signals });
  emit(DEPLOYMENT_EVENT, { scan: true });
  return signals;
}

// ── helpers ────
function avg(arr: number[]): number {
  if (!arr.length) return 0;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}
function clamp(n: number, min = 0, max = 100): number {
  return Math.max(min, Math.min(max, n));
}
